import { type ComponentType, type LazyExoticComponent, lazy } from "react";

// biome-ignore lint/suspicious/noExplicitAny: React.lazy's own bound; props are checked where the component renders.
type Component = ComponentType<any>;

/**
 * How long a cancelled chunk waits for the page to be replaced before it
 * counts as a real failure. Long enough to cover a reload on a slow
 * connection; a page still here after that was never going away (a download
 * link, a host choosing "Stay" on an unsaved-changes prompt) and must not
 * spin forever.
 */
export const CANCELLED_CHUNK_GRACE_MS = 10_000;

/**
 * Keeps the nearest Suspense boundary on its fallback while the reload or
 * navigation that cancelled the chunk replaces the document, and rejects if
 * the document is still here once the grace period is over.
 */
function awaitPageReplacement<T>(): Promise<T> {
  return new Promise<T>((_, reject) => {
    setTimeout(() => {
      reject(
        new Error(
          "A code-split screen's chunk load was cancelled, but the page was not replaced"
        )
      );
    }, CANCELLED_CHUNK_GRACE_MS);
  });
}

/**
 * `React.lazy` for a code-split screen, safe under the `vite:preloadError`
 * recovery in `main.tsx`.
 *
 * When that handler cancels the event (it is about to reload onto the new
 * deploy, or the page is already navigating away), Vite resolves the
 * `import()` with `undefined` instead of rejecting it. Plain `lazy` then reads
 * `.default` off `undefined`: the render throws, the error screen flashes up
 * before the reload lands, and Sentry records a crash that never happened.
 * Here a cancelled chunk suspends instead, so the loading screen stays up
 * until the page is replaced; if it never is, the load fails after
 * `CANCELLED_CHUNK_GRACE_MS`. A load that genuinely fails still rejects and
 * reaches the error boundary.
 *
 * Pass `exportName` for a module whose component is a named export.
 */
export function lazyChunk<T extends Component>(
  load: () => Promise<{ default: T }>
): LazyExoticComponent<T>;
export function lazyChunk<M extends Record<K, Component>, K extends keyof M>(
  load: () => Promise<M>,
  exportName: K
): LazyExoticComponent<M[K]>;
export function lazyChunk(
  load: () => Promise<Record<string, unknown>>,
  exportName = "default"
): LazyExoticComponent<Component> {
  return lazy(async () => {
    const module: Record<string, unknown> | undefined = await load();
    if (module === undefined) {
      return awaitPageReplacement<{ default: Component }>();
    }
    return { default: module[exportName] as Component };
  });
}
